import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import salesContinuousServiceService from '../../../services/salesContinuousServiceService';
import { supplierService } from '../../../services/supplierService';
import { useSalesPointOfSale } from '../../../contexts/SalesPointOfSaleContext';
import SupplierSelector from '../../../components/PurchaseOrders/SupplierSelector';
import Toast from '../../../components/Common/Toast';

const CreateVentasContinuousService = () => {
  const navigate = useNavigate();
  const { selectedPointOfSale } = useSalesPointOfSale();
  const [formData, setFormData] = useState({
    client_id: '',
    start_date: new Date().toISOString().split('T')[0],
    notes: '',
    include_areas: false,
  });
  const [clients, setClients] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const hasLoadedFormData = useRef(false);

  useEffect(() => {
    if (!hasLoadedFormData.current) {
      hasLoadedFormData.current = true;
      loadFormData();
    }
  }, []);

  const loadFormData = async () => {
    try {
      const res = await supplierService.getSuppliers({ active_only: true, entity_client: true });
      if (res.success) setClients(res.data || []);
    } catch {
      setToast({ isOpen: true, message: 'Error al cargar clientes', type: 'error' });
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: null }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.client_id) newErrors.client_id = 'El cliente es requerido';
    if (!formData.start_date) newErrors.start_date = 'La fecha de inicio es requerida';
    if (!selectedPointOfSale?.id) newErrors.point_of_sale = 'Seleccione un punto de venta';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      setToast({ isOpen: true, message: 'Complete los campos requeridos', type: 'error' });
      return;
    }

    setLoading(true);
    try {
      const submitData = {
        client_id: parseInt(formData.client_id, 10),
        point_of_sale_id: selectedPointOfSale.id,
        start_date: formData.start_date,
        notes: formData.notes || null,
        include_areas: !!formData.include_areas,
      };
      const response = await salesContinuousServiceService.createContinuousService(submitData);
      if (response.success) {
        setToast({ isOpen: true, message: 'Servicio continuo creado', type: 'success' });
        setTimeout(() => {
          navigate(`/ventas/continuous-services/${response.data.id}`, {
            state: { posConfirmed: true },
          });
        }, 1500);
      } else {
        setErrors(response.errors || {});
        setToast({ isOpen: true, message: response.message || 'Error al crear', type: 'error' });
      }
    } catch {
      setToast({ isOpen: true, message: 'Error al crear el servicio continuo', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800">Nuevo servicio continuo (ventas)</h1>
          <p className="text-gray-600 mt-1">Contrato de venta con registros de avance periódicos</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cliente <span className="text-red-500">*</span>
              </label>
              <SupplierSelector
                value={formData.client_id}
                onChange={(value) => handleInputChange('client_id', value)}
                suppliers={clients}
                error={errors.client_id}
              />
              {errors.client_id && <p className="mt-1 text-sm text-red-600">{errors.client_id}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fecha de inicio <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={formData.start_date}
                onChange={(e) => handleInputChange('start_date', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                  errors.start_date ? 'border-red-500' : 'border-gray-300'
                }`}
                required
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="include_areas"
                checked={formData.include_areas}
                onChange={(e) => handleInputChange('include_areas', e.target.checked)}
                className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
              />
              <label htmlFor="include_areas" className="ml-2 text-sm text-gray-700">
                Incluir áreas
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Observaciones</label>
              <textarea
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                placeholder="Observaciones adicionales..."
              />
            </div>

            {errors.point_of_sale && (
              <p className="text-sm text-red-600">{errors.point_of_sale}</p>
            )}
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={() => navigate('/ventas/continuous-services', { state: { posConfirmed: true } })}
              className="px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md font-medium"
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md font-medium disabled:opacity-50"
              disabled={loading}
            >
              {loading ? 'Creando...' : 'Crear servicio continuo'}
            </button>
          </div>
        </form>
      </div>

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </div>
  );
};

export default CreateVentasContinuousService;
